import type { ProgrammeAircraftDetailPublic } from "@common/client"
import { z } from "zod"
import type { ColumnSizing } from "@/lib/column-widths"
import type { GroupLabel } from "@/lib/grouping"
import {
  type SortValue,
  sortRows,
} from "@/routes/_layout/maintenance-programmes/-lib/sort"
import type { ProgrammeSearch } from "./search"
import { type Direction, withFixed } from "./table"

export type Enrolment = ProgrammeAircraftDetailPublic

export const AIRCRAFT_COLUMNS = [
  "registration",
  "serial",
  "model",
  "hours",
  "landings",
  "enrolled",
] as const

export const aircraftColumnSchema = z.enum(AIRCRAFT_COLUMNS)

export type AircraftColumn = z.infer<typeof aircraftColumnSchema>

export type AircraftSlot = AircraftColumn | "actions"

export const AIRCRAFT_COLUMN_LABELS: Record<AircraftColumn, string> = {
  registration: "Registration",
  serial: "Serial no.",
  model: "Model",
  hours: "Flight hours",
  landings: "Landings",
  enrolled: "Enrolled",
}

export const AIRCRAFT_SIZING: ColumnSizing<AircraftSlot> = {
  id: "programme-aircraft",
  widths: {
    registration: 7,
    serial: 8,
    model: 9,
    hours: 6.5,
    landings: 6,
    enrolled: 7,
    actions: 3.25,
  },
  fill: "enrolled",
  pinned: ["actions"],
  extras: ["actions"],
  dropOrder: ["serial", "landings", "hours", "model", "enrolled"],
}

export const AIRCRAFT_GROUPINGS = ["model"] as const

export const aircraftGroupingSchema = z.enum(AIRCRAFT_GROUPINGS)

export type AircraftGrouping = z.infer<typeof aircraftGroupingSchema>

export const AIRCRAFT_GROUPING_LABELS: Record<AircraftGrouping, string> = {
  model: "Model",
}

export type AircraftFilterValues = { aircraft_model?: string[] }

export const NO_AIRCRAFT_FILTERS: AircraftFilterValues = {
  aircraft_model: undefined,
}

export type AircraftDisplay = {
  columns: AircraftColumn[]
  sort: AircraftColumn
  direction: Direction
  group: AircraftGrouping | undefined
}

export function aircraftDisplayOf(search: ProgrammeSearch): AircraftDisplay {
  return {
    columns: search.aircraft_columns
      ? withFixed(AIRCRAFT_COLUMNS, "registration", search.aircraft_columns)
      : [...AIRCRAFT_COLUMNS],
    sort: search.aircraft_sort ?? "registration",
    direction: search.aircraft_direction ?? "asc",
    group: search.aircraft_group,
  }
}

export function hasAircraftFilters(values: AircraftFilterValues): boolean {
  return Boolean(values.aircraft_model?.length)
}

export function modelOf(enrolment: Enrolment): string | undefined {
  return enrolment.aircraft?.model
}

function sortValue(enrolment: Enrolment, column: AircraftColumn): SortValue {
  const { aircraft } = enrolment
  switch (column) {
    case "registration":
      return aircraft?.registration_no ?? null
    case "serial":
      return aircraft?.serial_no ?? null
    case "model":
      return aircraft?.model ?? null
    case "hours":
      return aircraft?.total_flight_hours ?? null
    case "landings":
      return aircraft?.total_landings ?? null
    case "enrolled":
      return enrolment.effective_from
  }
}

export function shownEnrolments(
  enrolments: Enrolment[],
  display: AircraftDisplay,
  filters: AircraftFilterValues,
): Enrolment[] {
  const models = filters.aircraft_model
  return sortRows(
    enrolments.filter((enrolment) => {
      const model = modelOf(enrolment)
      return !models?.length || (model !== undefined && models.includes(model))
    }),
    (enrolment) => sortValue(enrolment, display.sort),
    display.direction,
  )
}

export function enrolmentGroup(
  enrolment: Enrolment,
  grouping: AircraftGrouping,
): GroupLabel {
  switch (grouping) {
    case "model": {
      const model = modelOf(enrolment)
      return model
        ? { key: model, label: model }
        : { key: "", label: "No model", order: 1 }
    }
  }
}
